import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { RefreshCw, Plus, X, Loader2, Clock, CheckCircle2, XCircle, Package, Search, WifiOff, Bell } from 'lucide-react';
import { errorText } from '../health/healthConfig';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../../components/ui/dialog';
import { PortalDialogContent as DialogContent } from '../PortalDialogContent';
import { usePortalDestination, usePortalDraft } from '../PortalDestinationContext';
import { usePortalAuth } from '../PortalAuthContext';
import { PortalRequestDestination } from '../PortalIdentity';
import { PortalRxTabs } from '../PortalRxTabs';
import { PortalRefillNudges } from './PortalRefillNudges';
import { PortalEmptyState } from '../ui/PortalEmptyState';

const STATUS_META = {
  pending:   { label: 'Awaiting pharmacy', icon: Clock,        cls: 'bg-amber-100 text-amber-700' },
  approved:  { label: 'Approved',          icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-700' },
  ready:     { label: 'Ready for pickup',  icon: Package,      cls: 'bg-blue-100 text-blue-700' },
  collected: { label: 'Collected',         icon: CheckCircle2, cls: 'bg-slate-100 text-slate-600' },
  rejected:  { label: 'Not approved',      icon: XCircle,      cls: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Cancelled',         icon: XCircle,      cls: 'bg-slate-100 text-slate-600' },
};

const BRAND_ALIASES = {
  paracetamol: ['panadol', 'calpol', 'acetaminophen'],
  ibuprofen: ['brufen', 'advil', 'nurofen'],
  amoxicillin: ['amoxil', 'amoxycillin'],
  azithromycin: ['zithromax'],
  metronidazole: ['flagyl'],
  salbutamol: ['ventolin'],
};

export default function PortalRefillsPage() {
  const { api: portalApi, branch, ensureConnection } = usePortalDestination();
  const { patient, activeMember } = usePortalAuth();
  // Dose fields only appear when the request is for a young child.
  const childAge = (() => {
    if (!activeMember?.dob) return null;
    const years = (Date.now() - new Date(activeMember.dob).getTime()) / 31557600000;
    return years > 0 && years < 12 ? Math.round(years * 10) / 10 : null;
  })();
  const location = useLocation();
  const navigate = useNavigate();  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [items, setItems] = useState([{ uid: crypto.randomUUID(), product_name: '', quantity: 1 }]);
  const [notes, setNotes] = useState('');
  const [pickup, setPickup] = useState('');
  const [delivery, setDelivery] = useState(false);
  const [sourceSaleId, setSourceSaleId] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [remindedIds, setRemindedIds] = useState(new Set());
  const [doseGuide, setDoseGuide] = useState([]);
  const [addingReminderFor, setAddingReminderFor] = useState(null);
  const pickupDismissKey = `portal-pickup-reminder-dismissed:${patient?.patient_user_id || 'anon'}`;
  const [dismissedPickupIds, setDismissedPickupIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(pickupDismissKey) || '[]')); } catch { return new Set(); }
  });
  usePortalDraft(items.some(i => i.product_name.trim()) || notes.trim() || pickup || delivery, saving, (() => {
    const meds = items.map(i => i.product_name.trim()).filter(Boolean);
    if (meds.length) return `refill request for ${meds.join(', ')}`;
    if (notes.trim()) return 'refill request notes';
    return 'your refill request';
  })());

  const load = useCallback(async () => {
    setLoading(true); setLoadError('');
    try {
      const [refillsRes, remindersRes] = await Promise.all([
        portalApi.get('/refills'),
        portalApi.get('/reminders').catch(() => ({ data: { reminders: [] } })),
      ]);
      setRows(refillsRes.data.refills || []);
      setRemindedIds(new Set(
        (remindersRes.data.reminders || []).map((rem) => rem.source_request_id).filter(Boolean)
      ));
    } catch (e) {
      console.error('Failed to load refills:', e);
      setLoadError(errorText(e));
    } finally { setLoading(false); }
  }, [portalApi]);
  useEffect(() => { load(); }, [load]);

  // Usual doses for this child, so a parent sees them before typing.
  useEffect(() => {
    if (!childAge || !activeMember) { setDoseGuide([]); return; }
    portalApi.get(`/family/${activeMember.patient_user_id}/dose-guide`)
      .then((r) => setDoseGuide(r.data.medicines || []))
      .catch(() => setDoseGuide([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMember?.patient_user_id, childAge]);

  const guideFor = (name) => {
    const text = (name || '').toLowerCase().trim();
    if (text.length < 3) return null;
    return doseGuide.find((g) => text.includes(g.molecule)
      || (BRAND_ALIASES[g.molecule] || []).some((a) => text.includes(a))) || null;
  };

  const applyGuide = (index, guide) => {
    const copy = [...items];
    copy[index] = { ...copy[index], strength: guide.strength || '', dose: guide.suggested_dose, frequency: guide.suggested_frequency };
    setItems(copy);
  };

  // Prefill from Order History "Reorder" button (navigate with state)
  useEffect(() => {
    const p = location.state?.prefill;
    if (!p || !Array.isArray(p.items) || p.items.length === 0) return;
    const clean = p.items
      .map((i) => ({
        uid: crypto.randomUUID(),
        product_name: (i.product_name || i.name || '').trim(),
        quantity: Number(i.quantity) || 1,
      }))
      .filter((i) => i.product_name);
    if (clean.length === 0) return;
    setItems(clean);
    setSourceSaleId(p.source_sale_id || null);
    setNotes(p.notes || '');
    setOpen(true);
    // Clear the router state so a page refresh doesn't re-open the dialog
    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location.state, location.pathname, location.search, navigate]);

  const addItem = () => setItems([...items, { uid: crypto.randomUUID(), product_name: '', quantity: 1 }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  // One-tap reorder of a past collected refill — prefills the new-request
  // form with the same medications instead of making the patient retype them.
  const reorderRefill = (r) => {
    const cleanItems = (r.items || [])
      .map((i) => ({ uid: crypto.randomUUID(), product_name: i.product_name || '', quantity: Number(i.quantity) || 1 }))
      .filter((i) => i.product_name);
    if (cleanItems.length === 0) return;
    setItems(cleanItems);
    setSourceSaleId(null);
    setNotes('');
    setPickup('');
    setDelivery(false);
    setOpen(true);
  };

  // Suggested when a refill is marked ready for pickup — one tap adds a
  // reminder for tomorrow morning, editable afterwards on the Reminders page.
  const addPickupReminder = async (r) => {
    setAddingReminderFor(r.request_id);
    try {
      const meds = (r.items || []).map((i) => i.product_name).join(', ') || 'your refill';
      const due = new Date();
      due.setDate(due.getDate() + 1);
      due.setHours(9, 0, 0, 0);
      await portalApi.post('/reminders', {
        title: `Pick up ${meds}`.slice(0, 120),
        description: `Ready for pickup at ${r.branch_name}`,
        due_at: due.toISOString(),
        repeat: 'none',
        channels: ['push'],
        source_request_id: r.request_id,
      });
      setRemindedIds((prev) => new Set(prev).add(r.request_id));
      toast.success('Pickup reminder added');
    } catch {
      toast.error('Could not add that reminder');
    } finally {
      setAddingReminderFor(null);
    }
  };

  const dismissPickupReminder = (requestId) => {
    setDismissedPickupIds((prev) => {
      const next = new Set(prev).add(requestId);
      localStorage.setItem(pickupDismissKey, JSON.stringify([...next]));
      return next;
    });
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (!q) return true;
      const haystack = `${(r.items || []).map((i) => i.product_name).join(' ')} ${r.branch_name || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, search, statusFilter]);

  const submit = async () => {
    if (!branch) {
      toast.error('Choose a service branch first.');
      return;
    }
    const cleanItems = items
      .map((i) => ({
        product_name: (i.product_name || '').trim(),
        quantity: Number(i.quantity) || 1,
        ...(childAge ? {
          strength: (i.strength || '').trim(),
          dose: (i.dose || '').trim(),
          frequency: (i.frequency || '').trim(),
        } : {}),
      }))
      .filter((i) => i.product_name);
    if (cleanItems.length === 0) {
      toast.error('Add at least one medication.');
      return;
    }
    setSaving(true);
    try {
      if (!await ensureConnection()) return;
      const result = await portalApi.post('/refills', {
        tenant_id: branch.tenant_id,
        source_sale_id: sourceSaleId || undefined,
        items: cleanItems,
        preferred_pickup_date: pickup || undefined,
        delivery,
        notes,
      });
      const message = `Refill request sent to ${result.data.branch_name}.`;
      toast.success(message); setConfirmation(message);
      setItems([{ uid: crypto.randomUUID(), product_name: '', quantity: 1 }]);
      setNotes('');
      setPickup('');
      setDelivery(false);
      setSourceSaleId(null);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Request failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="portal-refills-page">
      <PortalRxTabs active="refills" />
      <PortalRequestDestination testId="portal-refills-destination" />
      <PortalRefillNudges />
      {confirmation && <p role="status" className="text-sm text-emerald-800" data-testid="portal-refill-confirmation">{confirmation}</p>}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Refills</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button disabled={!branch} className="bg-emerald-600 hover:bg-emerald-700" data-testid="portal-refill-new-btn">
              <Plus className="w-4 h-4 mr-1" /> New request
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="portal-refill-dialog">
            <DialogHeader>
              <DialogTitle>Request a refill</DialogTitle>
              <DialogDescription>List the medications you need. Your pharmacist will confirm availability.</DialogDescription>
            </DialogHeader>
            <PortalRequestDestination testId="portal-refill-form-destination" />
            {sourceSaleId && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs px-3 py-2" data-testid="portal-refill-reorder-badge">
                Reordering from a previous purchase — edit anything before submitting.
              </div>
            )}
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Medications</Label>
                {childAge && (
                  <p className="text-xs text-slate-500" data-testid="portal-refill-child-dose-hint">
                    Add the strength, dose and how often for {activeMember.name.split(' ')[0]} ({Math.floor(childAge)} yrs
                    {activeMember.weight_kg ? `, ${activeMember.weight_kg} kg` : ''}) —
                    your pharmacist checks it against the usual range for their age
                    {activeMember.weight_kg ? ' and weight' : ''}.
                    {!activeMember.weight_kg && ' Add their weight on the Family profiles page for a sharper check.'}
                  </p>
                )}
                {items.map((it, i) => (
                  <div key={it.uid} className="space-y-2" data-testid={`portal-refill-item-${i}`}>
                    <div className="flex gap-2">
                      <Input placeholder="Medication name" value={it.product_name} data-testid={`portal-refill-medication-${i}`}
                        onChange={(e) => { const c = [...items]; c[i].product_name = e.target.value; setItems(c); }} />
                      <Input type="number" min="1" className="w-20 shrink-0" value={it.quantity} data-testid={`portal-refill-quantity-${i}`}
                        onChange={(e) => { const c = [...items]; c[i].quantity = e.target.value; setItems(c); }} />
                      {items.length > 1 && (
                        <Button variant="ghost" size="icon" onClick={() => removeItem(i)} data-testid={`portal-refill-remove-${i}`}><X className="w-4 h-4" /></Button>
                      )}
                    </div>
                    {childAge && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <div>
                          <Label className="text-xs text-slate-500">Strength</Label>
                          <Input placeholder="e.g. 120mg/5ml" value={it.strength || ''} data-testid={`portal-refill-strength-${i}`}
                            onChange={(e) => { const c = [...items]; c[i].strength = e.target.value; setItems(c); }} />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Each dose</Label>
                          <Input placeholder="e.g. 5 ml" value={it.dose || ''} data-testid={`portal-refill-dose-${i}`}
                            onChange={(e) => { const c = [...items]; c[i].dose = e.target.value; setItems(c); }} />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">How often</Label>
                          <Input placeholder="e.g. 3 times a day" value={it.frequency || ''} data-testid={`portal-refill-frequency-${i}`}
                            onChange={(e) => { const c = [...items]; c[i].frequency = e.target.value; setItems(c); }} />
                        </div>
                      </div>
                    )}
                    {childAge && guideFor(it.product_name) && (() => {
                      const g = guideFor(it.product_name);
                      const applied = it.dose === g.suggested_dose && it.frequency === g.suggested_frequency;
                      return (
                        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 flex flex-wrap items-center gap-2"
                          data-testid={`portal-refill-dose-suggestion-${i}`}>
                          <span>
                            Usual for {activeMember.name.split(' ')[0]}
                            {g.basis === 'weight' ? ` (${activeMember.weight_kg} kg)` : ''}:
                            {' '}<strong>{g.suggested_dose}</strong> of {g.strength}, {g.suggested_frequency}
                          </span>
                          <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={applied}
                            onClick={() => applyGuide(i, g)} data-testid={`portal-refill-dose-apply-${i}`}>
                            {applied ? 'Applied' : 'Use this'}
                          </Button>
                        </div>
                      );
                    })()}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addItem} data-testid="portal-refill-add-medication"><Plus className="w-4 h-4 mr-1" /> Add another</Button>
              </div>
              <div>
                <Label>Preferred pickup date</Label>
                <Input type="date" value={pickup} onChange={(e) => setPickup(e.target.value)} data-testid="portal-refill-pickup" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="portal-refill-delivery" data-testid="portal-refill-delivery" checked={delivery} onChange={(e) => setDelivery(e.target.checked)} />
                <Label htmlFor="portal-refill-delivery">Delivery preferred (if available)</Label>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="portal-refill-notes" />
              </div>
              <Button onClick={submit} disabled={saving || !branch} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="portal-refill-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Submit request
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader><CardTitle>Your refill requests</CardTitle></CardHeader>
        <CardContent>
          <div className="portal-rx-filter-bar mb-3" data-testid="portal-refill-filter-bar">
            <div className="relative portal-rx-search">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search refills"
                className="pl-9" data-testid="portal-refill-search-input" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px] shrink-0" data-testid="portal-refill-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="portal-refill-status-all">All statuses</SelectItem>
                {Object.entries(STATUS_META).map(([key, meta]) => (
                  <SelectItem key={key} value={key} data-testid={`portal-refill-status-${key}`}>{meta.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {loading ? (
            <p className="text-sm text-slate-500 py-6 text-center" data-testid="portal-refill-loading">Loading refill requests…</p>
          ) : loadError ? (
            <PortalEmptyState icon={WifiOff} title="Couldn't load your refill requests" description={loadError}
              testId="portal-refill-load-error"
              action={<Button variant="outline" size="sm" onClick={load} data-testid="portal-refill-retry">Try again</Button>} />
          ) : filteredRows.length === 0 ? (
            <PortalEmptyState icon={Package}
              title={rows.length === 0 ? 'No refill requests yet' : 'No matches'}
              description={rows.length === 0 ? 'Request a refill and track its status here.' : 'Try a different search or status.'} />
          ) : null}
          <div className="space-y-2">
            {filteredRows.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.pending;
              return (
                <div key={r.request_id} className="portal-refill-row" data-testid={`portal-refill-${r.request_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap gap-2 items-center justify-between">
                      <div className="font-medium">
                        {(r.items || []).map((i) => `${i.product_name} × ${i.quantity}`).join(', ')}
                      </div>
                      <Badge className={meta.cls}><meta.icon className="w-3 h-3 mr-1" />{meta.label}</Badge>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Requested {new Date(r.created_at).toLocaleString()}
                      {r.preferred_pickup_date && <> · pickup {r.preferred_pickup_date}</>}
                      {r.delivery && <> · delivery preferred</>}
                    </div>
                    <div className="text-xs text-slate-600 mt-1" data-testid={`portal-refill-location-${r.request_id}`}>{r.branch_name}</div>
                    {r.decision?.note && (
                      <div className="text-xs mt-1 text-slate-600">Pharmacist: {r.decision.note}</div>
                    )}
                    {r.status === 'collected' && (
                      <Button
                        size="sm" variant="ghost"
                        disabled={!branch || branch.tenant_id !== r.tenant_id}
                        title={!branch || branch.tenant_id !== r.tenant_id ? 'Select this branch to reorder' : 'Request these medications again'}
                        onClick={() => reorderRefill(r)}
                        data-testid={`portal-refill-reorder-${r.request_id}`}
                        className="mt-2 text-emerald-700 hover:bg-emerald-50 h-8 px-2 -ml-2"
                      >
                        <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reorder
                      </Button>
                    )}
                    {r.status === 'ready' && !remindedIds.has(r.request_id) && !dismissedPickupIds.has(r.request_id) && (
                      <div className="mt-2 flex items-center gap-2 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2" data-testid={`portal-refill-pickup-suggestion-${r.request_id}`}>
                        <Bell className="w-4 h-4 text-blue-700 shrink-0" />
                        <p className="text-xs text-blue-800 flex-1">Want a reminder to collect it?</p>
                        <Button size="sm" disabled={addingReminderFor === r.request_id}
                          onClick={() => addPickupReminder(r)} data-testid={`portal-refill-pickup-remind-${r.request_id}`}
                          className="h-7 px-2.5 text-xs">
                          {addingReminderFor === r.request_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Remind me'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => dismissPickupReminder(r.request_id)}
                          data-testid={`portal-refill-pickup-dismiss-${r.request_id}`} className="h-7 px-2 text-xs">
                          Not now
                        </Button>
                      </div>
                    )}
                    {r.status === 'ready' && remindedIds.has(r.request_id) && (
                      <p className="text-xs text-blue-700 mt-2 flex items-center gap-1" data-testid={`portal-refill-pickup-reminded-${r.request_id}`}>
                        <Bell className="w-3.5 h-3.5" /> Pickup reminder set
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
